// ============================================================================
// Spacing Visualization — Label Pill Component
// ============================================================================

import { Box, Tooltip } from '@chakra-ui/react';
import { SPACING_VIZ_COLORS } from './spacingVizConstants';

export interface SpacingLabelProps {
  value: number;
  side: 'top' | 'right' | 'bottom' | 'left';
  type: 'margin' | 'padding';
  /** Original spacing token name (e.g. "lg", "compact"). Shown in tooltip on hover. */
  tokenName?: string;
}

export function SpacingLabel({ value, side, type, tokenName }: SpacingLabelProps): React.ReactElement | null {
  const isZero = value === 0;
  // Don't render labels for zero values — reduces clutter
  if (isZero) return null;

  const label = (
    <Box
      data-gc-testing-id={`spacing-label-${type}-${side}`}
      data-token={tokenName ?? undefined}
      as="span"
      position="absolute"
      fontSize="9px"
      fontFamily="mono"
      lineHeight="1"
      px="3px"
      py="1px"
      borderRadius="2px"
      bg={SPACING_VIZ_COLORS.labelBg}
      style={{ color: SPACING_VIZ_COLORS.labelText }}
      whiteSpace="nowrap"
      pointerEvents={tokenName ? 'auto' : 'none'}
      zIndex={2}
      {...getLabelPosition(side, type)}
    >
      {value}
    </Box>
  );

  if (tokenName) {
    return (
      <Tooltip
        label={`${type}: ${tokenName} → ${value}px`}
        placement="top"
        fontSize="10px"
        hasArrow
        openDelay={200}
      >
        {label}
      </Tooltip>
    );
  }

  return label;
}

function getLabelPosition(
  side: 'top' | 'right' | 'bottom' | 'left',
  type: 'margin' | 'padding',
): Record<string, string> {
  // Labels are centered in their region. Margin labels offset slightly outward
  // and padding labels slightly inward to avoid overlap when both exist.
  const isMargin = type === 'margin';
  switch (side) {
    case 'top':
      return { left: '50%', top: '50%', transform: `translate(${isMargin ? '-70%' : '-30%'}, -50%)` };
    case 'bottom':
      return { left: '50%', bottom: '50%', transform: `translate(${isMargin ? '-70%' : '-30%'}, 50%)` };
    case 'left':
      return { top: '50%', left: '50%', transform: `translate(-50%, ${isMargin ? '-70%' : '-30%'})` };
    case 'right':
      return { top: '50%', right: '50%', transform: `translate(50%, ${isMargin ? '-70%' : '-30%'})` };
  }
}
